// About ProteoRisk, in the same frame as the other webservers' about pages
// (the shared about header and template styles), with vector visuals instead
// of screenshots.
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import '../../UltimateHome/components/about/AboutContentHeader.css';
import '../../UltimateHome/components/about/AboutTemplate.css';
import './proteorisk.css';
import './proteorisk-about.css';
import { EXAMPLE_WHEEL } from './modules/exampleFiles';
import { DISCLAIMER, STATUS_LABEL } from './modules/copy';
import {
  MiniWheel, ScoreBar, ScoreGauge, StatusIcon, SystemIcon, ZAxis, ZScoreBar,
} from './modules/report/Visuals';


const STEPS = [
  { icon: 'blood', title: 'Blood', text: 'Plasma from one blood sample is measured on the Olink Explore HT panel.' },
  { icon: 'protein', title: 'Proteins', text: 'Up to 2,765 proteins are matched to the model and adjusted for age, sex and BMI.' },
  { icon: 'pattern', title: 'Patterns', text: 'The protein pattern is compared with patterns seen in data from the general population.' },
  { icon: 'conditions', title: 'Conditions', text: 'Each of 129 results gets a score from 0 to 100 and a tier: low, moderate or high.' },
];

function Reveal({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94], delay }}
    >
      {children}
    </motion.div>
  );
}

function HeaderVisual() {
  return (
    <div className="proteorisk-about-visual">
      <MiniWheel systems={EXAMPLE_WHEEL} className="large" />
      <div className="proteorisk-about-visual-list">
        {[
          [84, 'high', 'Your blood proteins look similar to those of people with high blood pressure.'],
          [63, 'moderate', 'Your blood proteins look similar to those of people with a cholesterol disorder.'],
        ].map(([score, tier, text]) => (
          <div key={score} className="proteorisk-about-visual-row">
            <ScoreGauge score100={score} tier={tier} size={70} />
            <div>
              <p>{text}</p>
              <span className={`PROTEORISK-chip ${tier}`}>
                <StatusIcon status={tier} size={11} />
                {STATUS_LABEL[tier]}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AboutHeader() {
  const visualRef = useRef(null);
  const [orbit, setOrbit] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    const update = () => {
      if (!visualRef.current) return;
      const rect = visualRef.current.getBoundingClientRect();
      setOrbit({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
    };
    update();
    window.addEventListener('resize', update);
    const timer = setTimeout(update, 100);
    return () => {
      window.removeEventListener('resize', update);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="about-content-header">
      <div className="about-content-header-nav">
        <Link to="/" className="about-content-header-back-btn">Back to Home</Link>
      </div>
      <div className="about-content-header-main">
        <div className="about-content-header-content">
          <h3 className="about-content-header-title">ProteoRisk</h3>
          <p className="about-content-header-subtitle">
            <span className="regular-word">Blood </span>
            <span className="highlight-word">protein risk scoring</span>
            <span className="regular-word"> across 83 conditions</span>
          </p>
          <div className="about-content-header-overview">
            <p>
              ProteoRisk reads the proteins measured in one blood sample on the Olink Explore HT panel and compares
              their pattern with patterns seen in people with 83 conditions. It returns 129 results, each scored from
              0 to 100: how closely your proteins resemble those of people with a condition today, and of people who
              developed it within ten years.
            </p>
            <p>
              Every protein is adjusted for age, sex and BMI first, so the comparison fits the person the sample came
              from. The results are grouped into 12 body systems, with the proteins behind each notable finding shown
              against their typical range.
            </p>
          </div>
        </div>
        <div ref={visualRef} className="about-content-header-image-container">
          <HeaderVisual />
        </div>
      </div>
      <div className="orbit-container" style={{ top: `${orbit.y}px`, left: `${orbit.x}px` }}>
        {[1, 2, 3, 4, 5, 6].map((index) => (
          <div key={index} className={`orbit-path orbit-path-${index}`}>
            <div className={`orbit-element orbit-element-${index}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ProteoRiskAbout() {
  return (
    <div className="PROTEORISK-theme-scope proteorisk-about-container">
      <AboutHeader />
      <div className="proteorisk-about-content">
        <Reveal>
          <section className="proteorisk-dynamic-section">
            <div className="image-left proteorisk-side-by-side-section">
              <div className="proteorisk-side-content">
                <div className="proteorisk-about-wheel proteorisk-side-image">
                  <MiniWheel systems={EXAMPLE_WHEEL} />
                </div>
                <div className="proteorisk-side-text">
                  <h2>One blood sample, a view across the body</h2>
                  <p>
                    Proteins do most of the work in the body, and many of them travel in the blood. If you think of the
                    body as a busy city, blood proteins are like the traffic on its main roads: their levels show which
                    areas are busy and which are quiet. ProteoRisk turns that picture into a wheel of 12 body systems,
                    each marked as looking typical or showing a moderate or strong pattern.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.1}>
          <section className="proteorisk-dynamic-section">
            <div className="proteorisk-features-grid-section">
              <h2>What you get</h2>
              <div className="proteorisk-features-grid">
                <div className="proteorisk-feature-item">
                  <div className="proteorisk-about-tile proteorisk-feature-image">
                    <ScoreGauge score100={84} tier="high" size={110} />
                    <ScoreGauge score100={57} tier="moderate" size={110} />
                  </div>
                  <div className="proteorisk-feature-content">
                    <h3>Report highlights</h3>
                    <p>Your strongest findings come first, each with a score from 0 to 100 and a plain sentence about what it means.</p>
                  </div>
                </div>
                <div className="proteorisk-feature-item">
                  <div className="proteorisk-about-tile list proteorisk-feature-image">
                    {[['Hypertension', 84, 'high'], ['Lipid metabolism', 63, 'moderate'], ['Angina', 11, 'low']].map(([name, score, tier]) => (
                      <div key={name} className="proteorisk-about-list-row">
                        <span>{name}</span>
                        <ScoreBar score100={score} tier={tier} />
                        <strong className={tier}>{score}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="proteorisk-feature-content">
                    <h3>Every condition, by body system</h3>
                    <p>Current findings and 10-year risk, grouped by body system, with moderate and high results on top.</p>
                  </div>
                </div>
                <div className="proteorisk-feature-item">
                  <div className="proteorisk-about-tile z proteorisk-feature-image">
                    <ZAxis />
                    {[[1.8, true], [0.6, false], [-2.3, true], [0.3, false]].map(([z, flagged]) => (
                      <ZScoreBar key={z} z={z} flagged={flagged} />
                    ))}
                  </div>
                  <div className="proteorisk-feature-content">
                    <h3>Proteins behind a finding</h3>
                    <p>For moderate and high results, the proteins that weigh most, each shown as a z-score against its typical range from -1.5 to 1.5.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.2}>
          <section className="proteorisk-dynamic-section">
            <div className="proteorisk-workflow-steps-section">
              <h2>How it works</h2>
              <div className="proteorisk-workflow-steps">
                {STEPS.map((step, index) => (
                  <div key={step.title} className="proteorisk-workflow-step">
                    <div className="proteorisk-step-number">{`Step ${index + 1}`}</div>
                    <div className="proteorisk-about-step-icon">
                      <SystemIcon code={step.icon} size={26} />
                    </div>
                    <div className="proteorisk-step-content">
                      <h3>{step.title}</h3>
                      <p>{step.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.3}>
          <section className="proteorisk-dynamic-section">
            <div className="proteorisk-stats-showcase-section">
              <h2>In numbers</h2>
              <div className="proteorisk-stats-grid">
                {[
                  ['5,348', 'proteins on the Explore HT panel'],
                  ['2,919', 'proteins in the model'],
                  ['129', 'results for 83 conditions'],
                  ['12', 'body systems'],
                ].map(([value, label]) => (
                  <div key={label} className="proteorisk-stat-item">
                    <div className="proteorisk-stat-value">{value}</div>
                    <div className="proteorisk-stat-label">{label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.4}>
          <section className="proteorisk-dynamic-section">
            <div className="proteorisk-tech-specs-section">
              <h2>At a glance</h2>
              <div className="proteorisk-tech-specs-grid">
                {[
                  ['Input', 'Olink Explore HT workbook (.xlsx)'],
                  ['Details', 'Age, sex and optional BMI'],
                  ['Results', 'Current findings and 10-year risk'],
                  ['Scores', '0 to 100, low, moderate or high'],
                  ['Analysis', 'XOmics olink-proteomics 2.0.0'],
                  ['Access', 'Web page and REST API'],
                ].map(([label, value]) => (
                  <div key={label} className="proteorisk-spec-item">
                    <div className="proteorisk-spec-label">{label}</div>
                    <div className="proteorisk-spec-value">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.5}>
          <section className="proteorisk-dynamic-section">
            <div className="proteorisk-about-note">
              <h2>Good to know</h2>
              <p>{DISCLAIMER}</p>
              <p>Share your results with your doctor, who can look at them together with your health history and other test results.</p>
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.6}>
          <section className="proteorisk-about-cta">
            <h2>Get Started</h2>
            <p>Ready to see which conditions your blood protein pattern resembles?</p>
            <div className="proteorisk-about-cta-actions">
              <Link to="/proteorisk" className="proteorisk-cta-button">Try ProteoRisk</Link>
              <Link to="/proteorisk/example" className="proteorisk-about-secondary">See an example report</Link>
            </div>
          </section>
        </Reveal>
      </div>
    </div>
  );
}
